import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Paper,
    Snackbar,
    Tab,
    Tabs,
    Typography
} from "@mui/material";

import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Collapse,
    Divider
} from "@mui/material";
import { useParams } from 'react-router-dom';


import { useEffect, useRef, useState } from "react";

import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import PreferencelistTableBlur from '../../../assets/images/PreferencelistTableBlur.png';
import { Header } from "../../../MarketingSiteComponents/Header";
import TrustStats from "../../../MarketingSiteComponents/TrustStats";
import {
    getAcapCollegeBranches,
    getAcapStudent,
    predictAcapAdmission
} from "../../../services/CollegePredictorServices/acapService";


const AcapPredictor = () => {
    const { ApplicationId } = useParams();

    const [applicationId, setApplicationId] = useState(ApplicationId);

    const [student, setStudent] = useState(null);

    const [analysis, setAnalysis] = useState(null);

    const [activePathway, setActivePathway] = useState("MHT");

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState("");

    const [success, setSuccess] = useState("");

    const [branchData, setBranchData] = useState({});
    const [branchLoading, setBranchLoading] = useState({});

    const [branchExpanded, setBranchExpanded] = useState({});
    const analyzeCalledRef = useRef(false);

    const handleAnalyze = async () => {

        const id = applicationId.trim().toUpperCase();

        if (!id) {
            setError("Please enter your ACAP Application ID.");
            return;
        }

        setLoading(true);

        setError("");
        setSuccess("");

        setStudent(null);
        setAnalysis(null);

        try {
            const studentResponse =
                await getAcapStudent(id);

            const analysisResponse =
                await predictAcapAdmission(id);


            setStudent(studentResponse);

            setAnalysis(analysisResponse);

            if (
                analysisResponse?.mht?.available
            ) {

                setActivePathway("MHT");

            } else if (
                analysisResponse?.jee?.available
            ) {

                setActivePathway("JEE");

            }


            setSuccess(
                "Your ACAP admission analysis is ready."
            );

        } catch (err) {

            console.error(
                "ACAP prediction error:",
                err
            );

            const message =
                err?.response?.data?.detail ||
                "Unable to analyze this Application ID. Please check the ID and try again.";

            setError(message);

        } finally {

            setLoading(false);

        }
    };

    const handleKeyDown = (event) => {

        if (
            event.key === "Enter" &&
            !loading
        ) {

            handleAnalyze();

        }
    };
    const getOverallSummary = (analysis) => {
        const mhtColleges = analysis?.mht?.colleges || [];
        const jeeColleges = analysis?.jee?.colleges || [];

        const collegeMap = new Map();

        [...mhtColleges, ...jeeColleges].forEach((college) => {
            const code = String(college.collegeCode);

            if (!collegeMap.has(code)) {
                collegeMap.set(code, college);
            } else {

                const existing = collegeMap.get(code);

                const priority = {
                    STRONG: 3,
                    POSSIBLE: 2,
                    DIFFICULT: 1,
                };

                if (
                    (priority[college.outlook] || 0) >
                    (priority[existing.outlook] || 0)
                ) {
                    collegeMap.set(code, college);
                }
            }
        });

        const colleges = Array.from(collegeMap.values());

        return {
            collegesAnalyzed: colleges.length,

            strongOptions: colleges.filter(
                college => college.outlook === "STRONG"
            ).length,

            possibleOptions: colleges.filter(
                college => college.outlook === "POSSIBLE"
            ).length,

            difficultOptions: colleges.filter(
                college => college.outlook === "DIFFICULT"
            ).length,
        };
    };


    const overallSummary = getOverallSummary(analysis);

    const handleReset = () => {

        setApplicationId("");

        setStudent(null);

        setAnalysis(null);

        setActivePathway("MHT");

        setError("");

        setSuccess("");

    };

    const formatNumber = (value) => {

        if (
            value === null ||
            value === undefined ||
            Number.isNaN(Number(value))
        ) {

            return "-";

        }

        return Number(value).toLocaleString(
            "en-IN"
        );

    };

    const mhtData =
        analysis?.mht || null;

    const jeeData =
        analysis?.jee || null;

    const chipColors = [
        { bg: "#DCEBFF", border: "#A8C8F5" },
        { bg: "#DDF3E3", border: "#A8D8B5" },
        { bg: "#FFE9CC", border: "#E8C38F" },
        { bg: "#E9DEFF", border: "#C5A9F0" },
    ];


    const mhtSummary =
        mhtData?.summary || {};


    const jeeSummary =
        jeeData?.summary || {};

    const activeData =
        activePathway === "JEE"
            ? jeeData
            : mhtData;

    const activeColleges = activeData?.colleges || [];

    useEffect(() => {
        if (!ApplicationId) {
            return;
        }

        if (analyzeCalledRef.current) {
            return;
        }

        analyzeCalledRef.current = true;

        handleAnalyze();
    }, [ApplicationId]);

    const handleViewBranchInsights = async (
        collegeCode,
        pathway
    ) => {

        const branchKey = `${collegeCode}-${pathway}`;

        if (branchData[branchKey]) {

            setBranchExpanded((prev) => ({
                ...prev,
                [branchKey]: !prev[branchKey],
            }));

            return;
        }

        setBranchLoading((prev) => ({
            ...prev,
            [branchKey]: true,
        }));

        try {

            const response =
                await getAcapCollegeBranches(
                    collegeCode,
                    pathway
                );

            setBranchData((prev) => ({
                ...prev,
                [branchKey]: response,
            }));

            setBranchExpanded((prev) => ({
                ...prev,
                [branchKey]: true,
            }));

        } catch (err) {

            console.error(
                "Branch API error:",
                err
            );

            setBranchData((prev) => ({
                ...prev,
                [branchKey]: {
                    error: true,
                },
            }));

        } finally {

            setBranchLoading((prev) => ({
                ...prev,
                [branchKey]: false,
            }));

        }
    };
    const renderCollegeCard = (college) => {

        const competition =
            college.competition || {};

        const branchPathway =
            college.pathway === "MHT-CET"
                ? "mhtcet"
                : "jee";

        const branchKey =
            `${college.collegeCode}-${branchPathway}`;

        return (

            <Card
                key={`${college.pathway}-${college.collegeCode}`}
                elevation={0}
                sx={{
                    border:
                        "1px solid #E3E8EF",
                    borderRadius: 3,
                    overflow: "hidden",
                    transition:
                        "all 0.2s ease",

                    "&:hover": {
                        borderColor:
                            "#B8C5D6",
                        boxShadow:
                            "0 5px 18px rgba(0,0,0,0.06)",
                    },
                }}
            >

                <CardContent
                    sx={{
                        p: {
                            xs: 2,
                            sm: 2.5,
                        },

                        "&:last-child": {
                            pb: 2.5,
                        },
                    }}
                >

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{
                            xs: "flex-start",
                            sm: "center",
                        }}
                        gap={2}
                    >

                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            minWidth={0}
                        >

                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    background:
                                        "#F3F6FA",
                                    display: "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    flexShrink: 0,
                                }}
                            >

                                <SchoolIcon
                                    sx={{
                                        color:
                                            "#425466",
                                    }}
                                />

                            </Box>


                            <Box minWidth={0}>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        textAlign: "left",
                                        fontSize: "1.4rem",
                                        fontWeight: 600,
                                        color: "#242e42",
                                    }}
                                >

                                    {college.collegeName}

                                </Typography>


                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.8}
                                    flexWrap="wrap"
                                    sx={{
                                        mt: 0.3,
                                    }}
                                >

                                    <Typography
                                        color="text.secondary"
                                        variant="body2"
                                        sx={{
                                            textAlign: "left",
                                            fontSize: "1.2rem",
                                            fontWeight: 600,
                                        }}
                                    >

                                        Code:{" "}
                                        {college.collegeCode}

                                    </Typography>


                                    {/* <Chip
                                        size="small"
                                        label={
                                            college.pathway ===
                                                "JEE"
                                                ? "JEE / All India"
                                                : "MHT-CET"
                                        }
                                        sx={{
                                            height: 22,
                                            fontSize: "1.2rem",
                                            fontWeight: 700,
                                        }}
                                    /> */}

                                </Box>

                            </Box>

                        </Box>

                    </Box>

                    <Box
                        sx={{
                            mt: 2,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                        }}
                    >
                        <Chip
                            label={`Institute Merit: ${formatNumber(college.instituteMerit)}`}
                            sx={{
                                height: 34,
                                fontSize: "1.4rem",
                                fontWeight: 700,
                                background: "#F3F6FA",
                                border: "1px solid #E3E8EF",
                                color: "#263238",
                            }}
                        />

                        <Chip
                            label={`Vacant Seats: ${formatNumber(college.seats)}`}
                            sx={{
                                height: 34,
                                fontSize: "1.4rem",
                                fontWeight: 700,
                                background: "#F1F8F3",
                                border: "1px solid #D8EBDD",
                                color: "#2E7D32",
                            }}
                        />

                        <Chip
                            label={`${formatNumber(competition.candidatesAheadWithOtherApplications || 0)} Students applied to other colleges`}
                            sx={{
                                height: 34,
                                fontSize: "1.4rem",
                                fontWeight: 700,
                                background: "#FFF8E1",
                                border: "1px solid #FFE5A3",
                                color: "#8A6500",
                            }}
                        />
                        <Button

                            size="medium"
                            onClick={() =>
                                handleViewBranchInsights(
                                    college.collegeCode,
                                    branchPathway
                                )
                            }
                            disabled={branchLoading[branchKey]}
                            endIcon={
                                branchLoading[branchKey]
                                    ? null
                                    : branchExpanded[branchKey]
                                        ? <ExpandLessIcon />
                                        : <ExpandMoreIcon />
                            }
                            sx={{
                                ml: "auto",
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: '1.3rem',
                                background: '#0B3054', // 'linear-gradient(135deg, hsl(45 100% 60%), hsl(18 90% 58%))',
                                color: '#fff',
                                "&:hover": {
                                    background: "#0B3054",
                                },

                                "&:disabled": {
                                    background: "#0B3054",
                                    color: "#fff",
                                },
                            }}
                        >
                            {branchLoading[branchKey] ? (
                                <>
                                    <CircularProgress
                                        size={18}
                                        sx={{ mr: 1 }}
                                    />
                                    Loading...
                                </>
                            ) : branchExpanded[branchKey] ? (
                                "Hide Branchwise seats"
                            ) : (
                                "View Branchwise seats"
                            )}
                        </Button>

                    </Box>
                    <Collapse
                        in={Boolean(branchExpanded[branchKey])}
                        timeout="auto"
                        unmountOnExit
                    >
                        {branchData[branchKey] &&
                            !branchData[branchKey].error && (

                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        borderRadius: 2,
                                        background: "#F8FAFC",
                                        border: "1px solid #E3E8EF",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mb: 1,
                                            textAlign: "left",
                                            fontSize: "1.4rem",
                                            fontWeight: 600,
                                            color: "#242e42",
                                        }}
                                    >
                                        Branch-wise Seats
                                    </Typography>

                                    {(
                                        branchData[branchKey]?.branches || []
                                    ).map((branch, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: "flex",
                                                justifyContent:
                                                    "space-between",
                                                alignItems: "center",
                                                gap: 2,
                                                p: 1.2,
                                                mb: 1,
                                                borderRadius: 1.5,
                                                background: "#FFFFFF",
                                                border:
                                                    "1px solid #E8EDF2",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                                                    fontSize: "1.3rem",
                                                    fontWeight: 500,
                                                    color: "#242e42",
                                                }}
                                            >
                                                {branch.branch}
                                            </Typography>

                                            <Chip
                                                size="medium"
                                                label={`${branch.seats} seats`}
                                                sx={{
                                                    flexShrink: 0,
                                                    fontWeight: 700,
                                                    fontSize: "1.3rem",
                                                    background:
                                                        branch.seats > 0
                                                            ? "#E8F5E9"
                                                            : "#FFEBEE",
                                                    color:
                                                        branch.seats > 0
                                                            ? "#2E7D32"
                                                            : "#C62828",
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                    </Collapse>
                    {college.outlook === "DIFFICULT" &&
                        college.higherCollegeAnalysis?.available && (
                            <HigherCollegeAnalysis
                                analysis={
                                    college.higherCollegeAnalysis
                                }
                            />
                        )}



                    <Box
                        sx={{
                            mt: 2,
                            pt: 2,
                            borderTop:
                                "1px solid #EDF0F3",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 2,
                                flexDirection: { xs: "column", sm: "row" },
                            }}
                        >

                            {/* right */}
                            {
                                competition
                                    .alternativeCollegeCounts
                                    ?.length > 0 && (
                                    <Box
                                        sx={{
                                            textAlign: { xs: "left", sm: "right" },
                                            flex: 1,
                                            width: { xs: "100%", sm: "auto" },
                                        }}
                                    >

                                        <Typography
                                            // variant="subtitle2"
                                            fontWeight={700}
                                            mb={1}
                                            variant="body2"
                                            sx={{
                                                fontSize: "1.3rem",
                                                // fontWeight: 500,
                                            }}
                                        >
                                            Colleges chosen by students ahead of you
                                        </Typography>


                                        <Box
                                            display="flex"
                                            flexWrap="wrap"
                                            gap={1}
                                            sx={{
                                                justifyContent: "flex-end",
                                            }}
                                        >

                                            {
                                                competition
                                                    .alternativeCollegeCounts
                                                    .map(
                                                        (
                                                            alternative,
                                                            index
                                                        ) => (

                                                            <Chip
                                                                key={`${alternative.college}-${index}`}
                                                                label={`${alternative.college} · ${alternative.candidateCount}`}
                                                                variant="outlined"
                                                                size="small"
                                                                sx={{
                                                                    fontSize: "1.3rem",
                                                                    fontWeight: 600,
                                                                    backgroundColor:
                                                                        chipColors[index % chipColors.length].bg,
                                                                    borderColor:
                                                                        chipColors[index % chipColors.length].border,
                                                                }}
                                                            />

                                                        )
                                                    )
                                            }

                                        </Box>

                                    </Box>
                                )}
                        </Box>

                    </Box>
                </CardContent>

            </Card >

        );

    };

    return (
        <>
            <Header isHideOptions={true} />
            <Box

                sx={{
                    width: "100%",
                    minHeight: "100vh",
                    backgroundColor:
                        "#F7F9FC",
                    pb: 6,
                    mt: 6,
                }}
            >

                <Box
                    sx={{
                        backgroundImage: `url(${PreferencelistTableBlur})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        position: "relative",
                        textAlign: "center",
                        py: {
                            xs: 4,
                            md: 10,
                        },
                        px: 2,
                        color: "#fff",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            bgcolor: "rgba(0,0,0,.45)",
                            zIndex: 0,
                        }}
                    />

                    <Box
                        sx={{
                            maxWidth: 1050,
                            mx: "auto",
                            position: "relative",
                            zIndex: 1,
                        }}
                    >

                        <Box
                            textAlign="center"
                            // maxWidth={750}
                            mx="auto"
                        >

                            {/* <Chip
                                icon={
                                    <SchoolIcon />
                                }
                                label="MHT-CET ACAP 2026"
                                sx={{
                                    mb: 2,
                                    background:
                                        "rgba(255,255,255,0.12)",
                                    color:
                                        "#FFFFFF",
                                    border:
                                        "1px solid rgba(255,255,255,0.2)",

                                    "& .MuiChip-icon": {
                                        color:
                                            "#FFFFFF",
                                    },
                                }}
                            /> */}


                            <Typography
                                sx={{
                                    fontSize: {
                                        xs: 23,
                                        md: 39,
                                    },
                                    color: '#FFC107',
                                    lineHeight: 1.2,
                                    fontWeight: 700,
                                    fontFamily: "Poppins",
                                }}
                            >

                                Find Your ACAP Admission Chances

                            </Typography>


                            <Typography
                                // sx={{
                                //     mt: 1.8,
                                //     color:
                                //         "rgba(255,255,255,0.78)",
                                //     fontSize: {
                                //         xs: "1.05rem",
                                //         sm: "1.25rem",
                                //         md: "2rem",
                                //     },
                                //     lineHeight: 1.7,
                                // }}
                                sx={{
                                    fontSize: {
                                        xs: 28,
                                        md: 44,
                                    },
                                    lineHeight: 2.2,
                                    fontWeight: 700,
                                    fontFamily: "Poppins",
                                }}
                            >
                                Assess MHT-CET & JEE admission chances.
                            </Typography>

                        </Box>

                        {/* <Paper
                            elevation={8}
                            sx={{
                                maxWidth: 700,
                                mx: "auto",
                                mt: 4,
                                p: {
                                    xs: 2,
                                    sm: 2.5,
                                },
                                borderRadius: 3,
                            }}
                        >

                            <Typography
                                variant="body2"
                                sx={{
                                    mb: 1,
                                    textAlign: "left",
                                    fontSize: "1.5rem",
                                    fontWeight: 500,
                                    color: "#242e42",
                                }}
                            >

                                Enter your ACAP Application ID

                            </Typography>


                            <Box
                                display="flex"
                                gap={1.2}
                                flexDirection={{
                                    xs: "column",
                                    sm: "row",
                                }}
                            >

                                <TextField
                                    fullWidth
                                    value={
                                        applicationId
                                    }
                                    onChange={(e) =>
                                        setApplicationId(
                                            e.target.value
                                        )
                                    }
                                    onKeyDown={
                                        handleKeyDown
                                    }
                                    placeholder="Enter Application ID"
                                    disabled={
                                        loading
                                    }
                                    size="medium"
                                    InputProps={{
                                        sx: {
                                            "& input": {
                                                fontSize: "1.5rem",
                                            },
                                        },
                                        startAdornment: (
                                            <InputAdornment
                                                position="start"
                                            >

                                                <SearchIcon
                                                    color="action"
                                                />

                                            </InputAdornment>
                                        ),
                                    }}
                                />


                                <Button
                                    variant="contained"
                                    onClick={
                                        handleAnalyze
                                    }
                                    disabled={
                                        loading ||
                                        !applicationId.trim()
                                    }
                                    sx={{
                                        minWidth: {
                                            xs: "100%",
                                            sm: 190,
                                        },
                                        minHeight: 52,
                                        borderRadius: 3,
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: '1.5rem',
                                        background: '#0B3054', // 'linear-gradient(135deg, hsl(45 100% 60%), hsl(18 90% 58%))',
                                        color: '#fff',
                                        "&:hover": {
                                            background: "#0B3054",
                                        }
                                    }}
                                >

                                    {
                                        loading ? (

                                            <>

                                                <CircularProgress
                                                    size={21}
                                                    color="inherit"
                                                    sx={{
                                                        mr: 1,
                                                    }}
                                                />

                                                Analysing...

                                            </>

                                        ) : (

                                            "Analyse My Chances"

                                        )
                                    }

                                </Button>

                            </Box>


                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.7}
                                mt={1.3}
                            >


                                <Typography
                                    variant="body2"
                                    sx={{
                                        // mb: 1,
                                        textAlign: "left",
                                        fontSize: "1.3rem",
                                        fontWeight: 500,
                                        color: "#242e42",
                                    }}
                                >
                                    <InfoOutlinedIcon
                                        sx={{
                                            fontSize: 16,
                                            color:
                                                "#78909C",
                                        }}
                                    />
                                    {" "}
                                    Your Application ID is needed to show analysis

                                </Typography>

                            </Box>
                        </Paper> */}
                        <TrustStats />

                    </Box>

                </Box>

                {analysis && (

                    <Box
                        sx={{
                            maxWidth: 1050,
                            mx: "auto",
                            px: {
                                xs: 2,
                                sm: 3,
                            },
                            mt: {
                                xs: 3,
                                md: 4,
                            },
                        }}
                    >

                        <Card
                            elevation={0}
                            sx={{
                                border:
                                    "1px solid #E3E8EF",
                                borderRadius: 3,
                            }}
                        >

                            <CardContent
                                sx={{
                                    p: {
                                        xs: 2,
                                        sm: 3,
                                    },
                                }}
                            >

                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems={{
                                        xs: "flex-start",
                                        sm: "center",
                                    }}
                                    gap={2}
                                    flexDirection={{
                                        xs: "column",
                                        sm: "row",
                                    }}
                                >

                                    <Box>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                mb: 1,
                                                textAlign: "left",
                                                fontSize: "1.4rem",
                                                fontWeight: 500,
                                                color: "#242e42",
                                            }}
                                        >
                                            Your ACAP Profile

                                        </Typography>


                                        <Typography
                                            variant="h5"
                                            fontWeight={800}
                                            sx={{
                                                mt: 0.2,
                                                textTransform: "capitalize",
                                            }}
                                        >

                                            {
                                                analysis
                                                    ?.student
                                                    ?.candidateName ||
                                                student
                                                    ?.candidateName ||
                                                "ACAP Applicant"
                                            }

                                        </Typography>




                                    </Box>


                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mb: 1,
                                            textAlign: "left",
                                            fontSize: "1.4rem",
                                            fontWeight: 600,
                                            color: "#242e42",
                                        }}
                                    >

                                        Application ID:{" "}

                                        <strong>

                                            {
                                                analysis
                                                    ?.student
                                                    ?.applicationId ||
                                                applicationId
                                            }

                                        </strong>

                                    </Typography>

                                </Box>
                                <Box
                                    display="flex"
                                    flexWrap="wrap"
                                    alignItems="center"
                                    gap={{ xs: 1, sm: 2 }}
                                    sx={{ mt: 0.5 }}
                                >
                                    {/* MHT-CET Merit */}
                                    <Box display="flex" alignItems="center" gap={0.6}>
                                        <Typography
                                            sx={{
                                                fontSize: "1.3rem",
                                                color: "#607D8B",
                                                fontWeight: 500,
                                            }}
                                        >
                                            MHT-CET Merit:
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: "1.3rem",
                                                color: "#0B3054",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {formatNumber(analysis?.student?.meritNo)}
                                        </Typography>
                                    </Box>
                                    |
                                    {/* Percentile */}
                                    <Box display="flex" alignItems="center" gap={0.6}>
                                        <Typography
                                            sx={{
                                                fontSize: "1.3rem",
                                                color: "#607D8B",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Percentile:
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: "1.3rem",
                                                color: "#0B3054",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {analysis?.student?.percentile ?? "-"}
                                        </Typography>
                                    </Box>
                                    |
                                    {/* Category */}
                                    <Box display="flex" alignItems="center" gap={0.6}>
                                        <Typography
                                            sx={{
                                                fontSize: "1.3rem",
                                                color: "#607D8B",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Category:
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: "1.3rem",
                                                color: "#0B3054",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {analysis?.student?.category || "-"}
                                        </Typography>
                                    </Box>
                                    |
                                    {/* Applied Colleges */}
                                    <Box display="flex" alignItems="center" gap={0.6}>
                                        <Typography
                                            sx={{
                                                fontSize: "1.3rem",
                                                color: "#607D8B",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Your Applied Colleges:
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: "1.3rem",
                                                color: "#0B3054",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {overallSummary.collegesAnalyzed}
                                        </Typography>
                                    </Box>
                                </Box>


                                {/* <Grid
                                    container
                                    spacing={1.5}
                                    sx={{
                                        mt: 2,
                                    }}
                                >

                                    <Grid
                                        item
                                        xs={6}
                                        sm={3}
                                    >

                                        <ProfileMetric
                                            label="MHT-CET Merit"
                                            value={
                                                formatNumber(
                                                    analysis
                                                        ?.student
                                                        ?.meritNo
                                                )
                                            }
                                        />

                                    </Grid>


                                    <Grid
                                        item
                                        xs={6}
                                        sm={3}
                                    >

                                        <ProfileMetric
                                            label="Percentile"
                                            value={
                                                analysis
                                                    ?.student
                                                    ?.percentile !==
                                                    null &&
                                                    analysis
                                                        ?.student
                                                        ?.percentile !==
                                                    undefined
                                                    ? analysis
                                                        .student
                                                        .percentile
                                                    : "-"
                                            }
                                        />

                                    </Grid>


                                    <Grid
                                        item
                                        xs={6}
                                        sm={3}
                                    >

                                        <ProfileMetric
                                            label="Category"
                                            value={
                                                analysis
                                                    ?.student
                                                    ?.category ||
                                                "-"
                                            }
                                        />

                                    </Grid>


                                    <Grid
                                        item
                                        xs={6}
                                        sm={3}
                                    >

                                        <ProfileMetric
                                            label="Your Applied Colleges"
                                            value={overallSummary.collegesAnalyzed}
                                        />

                                    </Grid>

                                </Grid> */}

                            </CardContent>

                        </Card>

                        <Box sx={{ mt: 4 }}>

                            <Typography
                                sx={{
                                    fontSize: "2rem",
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    color: "#242e42",
                                    // mb: 1,
                                    textAlign: "center",
                                    fontFamily: "inherit",
                                }}
                            >

                                College-wise Analysis

                            </Typography>


                            <Typography
                                sx={{
                                    mb: 1,
                                    textAlign: "center",
                                    fontSize: "1.4rem",
                                    fontWeight: 500,
                                    color: "#242e42",
                                }}
                            >
                                Compare admission possibilities: MHT-CET & JEE/All‑India
                            </Typography>

                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 2.5,
                                    border:
                                        "1px solid #E3E8EF",
                                    mb: 3,
                                    overflow: "hidden",
                                }}
                            >

                                <Tabs
                                    value={
                                        activePathway
                                    }
                                    onChange={(
                                        event,
                                        value
                                    ) =>
                                        setActivePathway(
                                            value
                                        )
                                    }
                                    variant="fullWidth"
                                >

                                    <Tab
                                        value="MHT"
                                        disabled={
                                            !mhtData?.available
                                        }
                                        label={
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                            >

                                                <span>
                                                    MHT-CET
                                                </span>


                                                <Chip
                                                    size="small"
                                                    label={
                                                        mhtSummary
                                                            ?.collegesAnalyzed ||
                                                        0
                                                    }
                                                    sx={{
                                                        height: "1.2rem",
                                                        fontWeight: 700,
                                                    }}
                                                />

                                            </Box>
                                        }
                                        sx={{
                                            // mb: 1,
                                            textAlign: "center",
                                            fontSize: "1.4rem",
                                            fontWeight: 600,
                                            fontFamily: "Roboto, Helvetica, Arial, sans-serif"
                                        }}
                                    />


                                    <Tab
                                        value="JEE"
                                        disabled={
                                            !jeeData?.available
                                        }
                                        label={
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                            >

                                                <span>
                                                    JEE / All India
                                                </span>


                                                <Chip
                                                    size="small"
                                                    label={
                                                        jeeSummary
                                                            ?.collegesAnalyzed ||
                                                        0
                                                    }
                                                    sx={{
                                                        height: "1.2rem",
                                                        fontWeight: 700,
                                                    }}
                                                />

                                            </Box>
                                        }
                                        sx={{
                                            textAlign: "center",
                                            fontSize: "1.4rem",
                                            fontWeight: 600,
                                            fontFamily: "Roboto, Helvetica, Arial, sans-serif"
                                        }}
                                    />

                                </Tabs>

                            </Paper>


                            {
                                activeColleges.length > 0 ? (

                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={2}
                                    >

                                        {
                                            activeColleges.map(
                                                renderCollegeCard
                                            )
                                        }

                                    </Box>

                                ) : (

                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 4,
                                            textAlign:
                                                "center",
                                            borderRadius: 3,
                                            border:
                                                "1px dashed #CBD5E1",
                                        }}
                                    >

                                        <SchoolIcon
                                            sx={{
                                                fontSize: 40,
                                                color:
                                                    "#94A3B8",
                                            }}
                                        />


                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                            sx={{
                                                mt: 1,
                                            }}
                                        >

                                            No colleges available

                                        </Typography>


                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                mt: 0.5,
                                            }}
                                        >

                                            We currently do not have
                                            application data available
                                            for this admission pathway.

                                        </Typography>

                                    </Paper>

                                )
                            }

                        </Box>

                    </Box>

                )
                }

                {
                    !analysis && !loading && (

                        <Box
                            sx={{
                                maxWidth: 850,
                                mx: "auto",
                                px: 2,
                                py: {
                                    xs: 5,
                                    md: 8,
                                },
                                textAlign: "center",
                            }}
                        >

                            <Box
                                sx={{
                                    width: 70,
                                    height: 70,
                                    borderRadius:
                                        "50%",
                                    background:
                                        "#EEF3F8",
                                    mx: "auto",
                                    display: "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                }}
                            >

                                <TrendingUpIcon
                                    sx={{
                                        fontSize: 34,
                                        color:
                                            "#526777",
                                    }}
                                />

                            </Box>


                            <Typography
                                variant="body1"
                                fontWeight={800}
                                sx={{
                                    mt: 2,
                                    fontSize: "1.5rem",
                                    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                                }}
                            >

                                Ready to analyse your chances?

                            </Typography>


                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{
                                    mt: 1,
                                    maxWidth: 600,
                                    mx: "auto",
                                    lineHeight: 1.7,
                                    fontSize: "1.4rem",
                                    fontFamily: "Roboto, Helvetica, Arial, sans-serif"
                                }}
                            >

                                Enter your ACAP Application ID above.
                                We'll compare your position with
                                available seats and analyse the
                                competition from candidates ahead
                                of you across MHT-CET and JEE pathways.

                            </Typography>

                        </Box>

                    )
                }

                {
                    loading && (

                        <Box
                            sx={{
                                maxWidth: 850,
                                mx: "auto",
                                px: 2,
                                py: 6,
                                textAlign: "center",
                            }}
                        >

                            <CircularProgress />


                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{
                                    mt: 2,
                                    fontSize: "1.4rem", fontFamily: "Roboto, Helvetica, Arial, sans-serif"
                                }}
                            >

                                Please Wait Analysing your ACAP chances...

                            </Typography>


                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    mt: 0.7,
                                    fontSize: "1.4rem", fontFamily: "Roboto, Helvetica, Arial, sans-serif"
                                }}
                            >

                                Comparing your MHT-CET and JEE
                                institute merit with available seats
                                and analysing applicant overlap.

                            </Typography>

                        </Box>

                    )
                }

                <Snackbar
                    open={Boolean(error)}
                    autoHideDuration={6000}
                    onClose={() =>
                        setError("")
                    }
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                >

                    <Alert
                        severity="error"
                        variant="filled"
                        onClose={() =>
                            setError("")
                        }
                        sx={{ fontSize: "15px", fontFamily: "Roboto, Helvetica, Arial, sans-serif" }}
                    >

                        {error}

                    </Alert>

                </Snackbar>

                <Snackbar
                    open={Boolean(success)}
                    autoHideDuration={3000}
                    onClose={() =>
                        setSuccess("")
                    }
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                >

                    <Alert
                        severity="success"
                        variant="filled"
                        onClose={() =>
                            setSuccess("")
                        }
                        sx={{ fontSize: "15px", fontFamily: "Roboto, Helvetica, Arial, sans-serif" }}
                    >

                        {success}

                    </Alert>

                </Snackbar>

            </Box >
        </>

    );

};

const ProfileMetric = ({
    label,
    value,
}) => {

    return (

        <Box
            sx={{
                p: 1.5,
                borderRadius: 2,
                background:
                    "#F7F9FC",
                border:
                    "1px solid #E8EDF2",
            }}
        >

            <Typography
                variant="body2"
                sx={{
                    textAlign: "center",
                    fontSize: "1.4rem",
                    fontWeight: 500,
                    color: "#242e42",
                    fontFamily: "Roboto, Helvetica, Arial, sans-serif"
                }}
            >

                {label}

            </Typography>


            <Typography
                variant="h6"
                fontWeight={800}
                sx={{
                    mt: 0.3,
                    textAlign: "center",
                    fontSize: "1.6rem",
                    color: "#242e42",
                    fontFamily: "Roboto, Helvetica, Arial, sans-serif"
                }}
            >

                {value}

            </Typography>

        </Box>

    );

};

export default AcapPredictor;

const HigherCollegeAnalysis = ({
    analysis
}) => {

    const [expanded, setExpanded] = useState(false);

    if (!analysis) {
        return null;
    }

    const general =
        analysis.general || {};

    const generalTotalSeats =
        Number(
            general.totalSeats || 0
        );

    const higherCollegesCount =
        Number(
            general.collegesCount || 0
        );




    const generalColleges =
        general.colleges || [];

    const handleToggle = () => {

        setExpanded(
            previous => !previous
        );
    };

    const formatNumber = (value) => {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN"
        );
    };

    return (

        <Box
            sx={{
                mt: 2,
                p: 2,
                borderRadius: 2.5,
                border:
                    "1px solid #E1E7EE",
                background:
                    "#FAFBFC"
            }}
        >

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{
                    xs: "flex-start",
                    sm: "center"
                }}
                gap={1.5}
                flexWrap="wrap"
            >
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1.2}
                    minWidth={0}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            background: "#EEF3F8",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0
                        }}
                    >
                        <AccountBalanceIcon
                            sx={{
                                color: "#526777"
                            }}
                        />
                    </Box>

                    <Box minWidth={0}>
                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: "left",
                                fontSize: "1.4rem",
                                fontWeight: 600,
                                color: "#242e42",
                                fontFamily:
                                    "Roboto, Helvetica, Arial, sans-serif"
                            }}
                        >
                            Comparable college
                        </Typography>

                    </Box>
                </Box>

                {/* Compact stats */}
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                    sx={{
                        ml: "auto"
                    }}
                >
                    <Chip
                        size="small"
                        label={`Comparable colleges: ${formatNumber(higherCollegesCount)}`}
                        sx={{
                            height: 28,
                            fontSize: "1.4rem",
                            fontWeight: 700,
                            background: "#F1F4F7",
                            color: "#455A64",
                            border: "1px solid #E1E7EE"
                        }}
                    />

                    <Chip
                        size="small"
                        label={`Total Vacant Seats: ${formatNumber(generalTotalSeats)}`}
                        sx={{
                            height: 28,
                            fontSize: "1.4rem",
                            fontWeight: 700,
                            background: "#F1F4F7",
                            color: "#455A64",
                            border: "1px solid #E1E7EE"
                        }}
                    />
                </Box>
            </Box>




            <Box
                onClick={
                    handleToggle
                }
                sx={{
                    mt: 1.5,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                        "center",
                    cursor: "pointer",
                    // borderRadius: 2,
                    // border:
                    //     "1px solid #E1E7EE",
                    background:
                        "#FFFFFF",

                    "&:hover": {
                        background:
                            "#F7F9FC"
                    }
                }}
            >

                <Typography
                    variant="body2"
                    fontWeight={700}
                    color="#455A64"
                    sx={{
                        fontSize: "1.4rem",
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif"
                    }}
                >
                    {expanded
                        ? "Hide comparable college details"
                        : "View comparable college details"
                    }
                </Typography>

                {expanded
                    ? (
                        <ExpandLessIcon
                            sx={{
                                ml: 0.5,
                                fontSize: 20,
                                color:
                                    "#607D8B"
                            }}
                        />
                    )
                    : (
                        <ExpandMoreIcon
                            sx={{
                                ml: 0.5,
                                fontSize: 20,
                                color:
                                    "#607D8B"
                            }}
                        />
                    )
                }

            </Box>

            <Collapse
                in={expanded}
                timeout="auto"
                unmountOnExit
            >

                <Divider
                    sx={{
                        my: 2
                    }}
                />


                <Box>


                    {generalColleges.length > 0 ? (

                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={1.2}
                        >

                            {generalColleges.map(
                                (
                                    college,
                                    index
                                ) => {
                                    const generalSeats =
                                        Number(
                                            college.seats ||
                                            0
                                        );

                                    return (

                                        <Box
                                            key={
                                                college.collegeCode ||
                                                index
                                            }
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 2,
                                                border:
                                                    "1px solid #E5EAF0",
                                                background:
                                                    "#FFFFFF"
                                            }}
                                        >

                                            <Box
                                                display="flex"
                                                alignItems="flex-start"
                                                gap={1}
                                            >

                                                <Box
                                                    sx={{
                                                        width: 34,
                                                        height: 34,
                                                        borderRadius: 1.5,
                                                        background:
                                                            "#F1F4F7",
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        justifyContent:
                                                            "center",
                                                        flexShrink: 0
                                                    }}
                                                >

                                                    <Typography
                                                        variant="caption"
                                                        fontWeight={800}
                                                        color="#607D8B"
                                                    >
                                                        {index + 1}
                                                    </Typography>

                                                </Box>


                                                <Box
                                                    minWidth={0}
                                                    flex={1}
                                                >

                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={800}
                                                        sx={{

                                                            textAlign: "left",
                                                            fontSize: "1.3rem",
                                                            color: "#242e42",
                                                        }}
                                                    >
                                                        {
                                                            college.collegeName
                                                        }
                                                    </Typography>

                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            display:
                                                                "block",
                                                            mt: 0.2,

                                                            textAlign: "left",
                                                            fontSize: "1.3rem",
                                                            color: "#242e42",
                                                        }}
                                                    >
                                                        College Code:{" "}
                                                        {
                                                            college.collegeCode
                                                        }
                                                        {" "} | {" "}
                                                        Vacant Seats:{" "}
                                                        {
                                                            formatNumber(
                                                                generalSeats
                                                            )
                                                        }
                                                    </Typography>

                                                </Box>

                                            </Box>

                                        </Box>

                                    );

                                }
                            )}

                        </Box>

                    ) : (

                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                background:
                                    "#F7F9FC",
                                textAlign:
                                    "center"
                            }}
                        >

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                No higher college seat
                                information is available.
                            </Typography>

                        </Box>

                    )}

                </Box>

            </Collapse>

        </Box>
    );
};




